/**
 * CinematicState — Persists cinematic completion state in a JSON file.
 *
 * In dev: auto-syncs to public/cinematic-state.json via Vite endpoint.
 * In prod: loaded from cinematic-state.json at boot, stored in memory.
 * NOT in localStorage — survives localStorage.clear().
 */

let _completed = new Set<string>();      // persisted completions
let _pending = new Set<string>();        // completed this session but not yet confirmed by level victory
let _syncTimer: ReturnType<typeof setTimeout> | null = null;

/** Load completion state from JSON file (called at boot) */
export async function loadCinematicState(): Promise<void> {
  try {
    const resp = await fetch('/cinematic-state.json');
    if (!resp.ok) return;
    const data: { completed: string[] } = await resp.json();
    if (Array.isArray(data.completed)) {
      _completed = new Set(data.completed);
    }
  } catch (e) { console.warn('[CinematicState] loadCompletedCinematics failed:', e); }
}

/** Check if a cinematic has been completed */
export function isCinematicCompleted(sequenceId: string): boolean {
  return _completed.has(sequenceId);
}

/** Mark a cinematic as completed for this session (not persisted until level victory) */
export function markCinematicCompleted(sequenceId: string): void {
  if (_completed.has(sequenceId)) return;
  _completed.add(sequenceId);  // prevents replay within same session
  _pending.add(sequenceId);    // awaits level victory to persist
}

/** Reset a single cinematic's completion and persist */
export function resetCinematicCompletion(sequenceId: string): void {
  _completed.delete(sequenceId);
  persistState();
}

/** Reset ALL cinematic completions and persist */
export function resetAllCinematicCompletions(): void {
  _completed.clear();
  persistState();
}

/** Persist pending completions after level victory — call this on win */
export function confirmPendingCompletions(): void {
  if (_pending.size === 0) return;
  _pending.clear();
  persistState();
}

/** Rollback pending completions (player quit/lost without finishing) */
export function rollbackPendingCompletions(): void {
  for (const id of _pending) {
    _completed.delete(id);
  }
  _pending.clear();
}

/** Get all completed sequence IDs (for export/debug) */
export function getCompletedCinematics(): string[] {
  return [..._completed];
}

/** Debounced persist to JSON via Vite dev endpoint */
function persistState(): void {
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    const json = JSON.stringify({ completed: [..._completed] }, null, 2);
    fetch('/api/save-cinematic-state', { method: 'POST', body: json })
      .then(r => { if (!r.ok) console.warn('[CinematicState] save failed:', r.status); })
      .catch(() => { /* prod or dev server not running — ignore */ });
  }, 300);
}
